import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Alert, IconButton, Tooltip, InputAdornment,
  FormControlLabel, Switch,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import {
  fetchAiTokenData, generateAiToken, revokeAiToken,
  setSelectedAiToken, clearAiTokenData, clearMessages, clearNewPlainToken,
} from '../reduxStore/AiApiTokenSlice';
import { AiApiToken, User } from '../models/models';
import { TableHeader, TableAction } from '../constants/interfaces';
import { ButtonType } from '../constants/enums';
import { getLabels } from '../constants/fieldLabels';
import MyTable from '../components/MyTable';
import SelectUser from '../components/Selects/SelectUser';
import { useDialog } from '../components/DialogComponent';
import { useSnackbar } from '../components/SnackbarProvider';
import AppPage from '../components/layout/AppPage';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppSurface from '../components/layout/AppSurface';

const GestionAiTokens: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { aiTokenData, total, loading, error, successMessage, newPlainToken } =
    useSelector((state: RootState) => state.AiApiTokenSlice);

  const [fetchParams] = useState({ limit: 100, offset: 0 });
  const [nom, setNom] = useState('');
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [copied, setCopied] = useState(false);
  const [hideRevoked, setHideRevoked] = useState(true);

  const [CreateDialog, openCreateDialog, closeCreateDialog] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });
  // Pas de bouton de fermeture rapide : on force un clic explicite après copie
  // du secret, pour éviter qu'il se referme par erreur (clic hors zone / échap)
  // avant que l'utilisateur ait pu le copier.
  const [SecretDialog, openSecretDialog, closeSecretDialog] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
    showCloseButton: false,
  });

  useEffect(() => {
    dispatch(fetchAiTokenData(fetchParams));
    return () => {
      dispatch(setSelectedAiToken(null));
      dispatch(clearAiTokenData());
    };
  }, [dispatch, fetchParams, successMessage]);

  useEffect(() => {
    if (error) { showSnackbar(error, 'error'); dispatch(clearMessages()); }
  }, [error, dispatch, showSnackbar]);

  useEffect(() => {
    if (newPlainToken) {
      closeCreateDialog();
      openSecretDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPlainToken]);

  const handleGenerate = async () => {
    if (!nom.trim()) return;
    await dispatch(generateAiToken({ nom: nom.trim(), user_id: targetUser?.id }));
    setNom('');
    setTargetUser(null);
  };

  const handleRevoke = (token: AiApiToken) => {
    if (!token.id) return;
    if (!window.confirm(`Révoquer le token "${token.nom}" ? Cette action est immédiate et irréversible.`)) return;
    dispatch(revokeAiToken(token.id));
  };

  const handleCloseSecret = () => {
    closeSecretDialog();
    dispatch(clearNewPlainToken());
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!newPlainToken) return;
    try {
      await navigator.clipboard.writeText(newPlainToken);
      setCopied(true);
    } catch {
      // Presse-papier indisponible (contexte non sécurisé, permission refusée) —
      // le champ reste sélectionnable manuellement (onFocus → select()).
    }
  };

  const headers: TableHeader<AiApiToken>[] = [
    { key: 'nom', label: 'Nom' },
    { key: 'login', label: 'Généré pour' },
    { key: 'actif', label: 'Actif', type: 'boolean' },
    { key: 'derniere_utilisation', label: 'Dernière utilisation' },
    { key: 'date_creation', label: 'Créé le' },
  ];

  const tableActions: TableAction<AiApiToken>[] = [
    {
      label: 'Révoquer',
      callback: handleRevoke,
      type: ButtonType.DELETE,
      hidden: (t) => !t.actif,
    },
  ];

  const visibleTokens = useMemo(
    () => (hideRevoked ? aiTokenData.filter((t) => t.actif) : aiTokenData),
    [aiTokenData, hideRevoked]
  );

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Tokens IA (connecteur MCP)"
        subtitle={`${total} token${total > 1 ? 's' : ''}`}
        icon={<SmartToyIcon />}
        addLabel="Générer un token"
        onAdd={openCreateDialog}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Un token permet à un agent IA externe (au choix du client, à ses frais) de se connecter en
        lecture à cet ERP via le connecteur MCP. Il hérite exactement des permissions et des
        restrictions de l'utilisateur pour lequel il a été généré — jamais plus.
      </Alert>

      <FormControlLabel
        sx={{ mb: 1 }}
        control={<Switch checked={hideRevoked} onChange={(e) => setHideRevoked(e.target.checked)} />}
        label="Masquer les tokens révoqués"
      />

      {loading && <Typography mb={2}>Chargement…</Typography>}

      <AppSurface>
        <MyTable
          headers={headers}
          data={visibleTokens}
          actions={tableActions}
          totalRows={visibleTokens.length}
          onPageChange={() => {}}
          showDetails
          fieldLabels={getLabels('AiApiToken')}
        />
      </AppSurface>

      <CreateDialog>
        <DialogTitle>Générer un nouveau token IA</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Donnez un nom pour identifier l'agent qui utilisera ce token (ex. "Claude Desktop").
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Nom"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleGenerate(); }}
          />

          <Typography variant="body2" color="text.secondary">
            Générer pour (par défaut : vous-même). Le token héritera exactement des permissions et
            restrictions de l'utilisateur choisi — préférez un compte restreint plutôt qu'un compte
            admin si l'agent n'a besoin que d'un accès limité.
          </Typography>
          <SelectUser selectedUser={targetUser} onUserChange={setTargetUser} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Annuler</Button>
          <Button variant="contained" onClick={handleGenerate} disabled={!nom.trim() || loading}>
            Générer
          </Button>
        </DialogActions>
      </CreateDialog>

      <SecretDialog>
        <DialogTitle>Token généré</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ce secret ne sera plus jamais affiché. Copiez-le maintenant et collez-le dans la
            configuration de votre agent IA.
          </Alert>
          <TextField
            fullWidth
            value={newPlainToken ?? ''}
            onFocus={(e) => e.target.select()}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: 13 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copier">
                    <IconButton onClick={handleCopy} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          {copied && (
            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
              Copié !
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseSecret}>
            J'ai copié le token, fermer
          </Button>
        </DialogActions>
      </SecretDialog>
    </AppPage>
  );
};

export default GestionAiTokens;
